import React, { useState } from 'react';
import { Grid, Typography, TextField, Button, CircularProgress, Collapse, IconButton } from '@material-ui/core';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import { getAffinityUserDetails, mapRenewalDataToFormData } from '../helpers/renewal';

function RenewalSearchComponent({ renewalConfig, packages, history, onPrefill }) {
    const [showSearch, setShowSearch] = useState(false);
    const [fieldValues, setFieldValues] = useState({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [multipleResults, setMultipleResults] = useState(null);

    if (!renewalConfig || !renewalConfig.enabled) return null;

    const searchFields = [
        { key: 'mobilenumber', label: 'Mobile Number', placeholder: 'Enter mobile number', maxLength: 10, pattern: '^[0-9]{10}$', errorMessage: 'Please enter a valid 10-digit mobile number' },
        { key: 'subscriptionId', label: 'Subscription ID', placeholder: 'Enter subscription ID' }
    ];
    const minRequired = 1;

    const handleFieldChange = (key, value) => {
        setFieldValues(prev => ({ ...prev, [key]: value }));
        setError('');
        setSuccess(false);
    };

    const handleSearch = async () => {
        const filledFields = searchFields.filter(f => fieldValues[f.key] && fieldValues[f.key].trim());
        if (filledFields.length < minRequired) {
            setError('Please enter at least one field to search');
            return;
        }

        for (const field of filledFields) {
            if (field.pattern) {
                const regex = new RegExp(field.pattern);
                if (!regex.test(fieldValues[field.key])) {
                    setError(field.errorMessage || `${field.label} is not valid`);
                    return;
                }
            }
        }

        const searchParams = {};
        filledFields.forEach(f => { searchParams[f.key] = fieldValues[f.key].trim(); });

        setLoading(true);
        setError('');
        setMultipleResults(null);
        try {
            const response = await getAffinityUserDetails(searchParams, true);
            if (response.success === 1 && response.data) {
                const userDetails = Array.isArray(response.data) ? response.data : [response.data];
                if (onPrefill) {
                    const mappedResults = userDetails.map(u => {
                        const mapped = mapRenewalDataToFormData(u);
                        return { userData: u, mapped };
                    }).filter(r => r.mapped);
                    if (mappedResults.length === 0) {
                        setError('No details found. Please try again or fill the form manually.');
                    } else if (mappedResults.length === 1) {
                        await proceedWithUserData(mappedResults[0].userData);
                    } else {
                        setMultipleResults(mappedResults);
                    }
                } else {
                    const mappedResults = userDetails.map(u => {
                        const mapped = mapRenewalDataToFormData(u);
                        const matchedPackage = mapped && mapped.packageId && packages
                            ? packages.find(p => p.id.toString() === mapped.packageId.toString())
                            : null;
                        return { userData: u, mapped, matchedPackage };
                    }).filter(r => r.mapped && r.matchedPackage);
                    if (mappedResults.length === 0) {
                        setError('Could not match your details to an available plan. Please fill the form manually.');
                    } else if (mappedResults.length === 1) {
                        await proceedWithUserData(mappedResults[0].userData);
                    } else {
                        setMultipleResults(mappedResults);
                    }
                }
            } else {
                setError(response.message || 'No details found. Please try again or fill the form manually.');
            }
        } catch (e) {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const proceedWithUserData = async (userData) => {
        const mapped = mapRenewalDataToFormData(userData);
        if (onPrefill) {
            setSuccess(true);
            onPrefill(mapped.prefillData, mapped.renewalId);
            return;
        }
        if (mapped && mapped.packageId && packages) {
            const matchedPackage = packages.find(p => p.id.toString() === mapped.packageId.toString());
            if (matchedPackage) {
                setSuccess(true);
                setTimeout(() => {
                    history.push('/Checkout', {
                        params: {
                            packageDetails: matchedPackage,
                            prefillData: mapped.prefillData,
                            isRenewal: true,
                            renewalId: mapped.renewalId
                        }
                    });
                }, 1500);
                return;
            }
        }
        setError('Could not match your details to an available plan. Please fill the form manually.');
    };

    const handleSelectResult = (result) => {
        setMultipleResults(null);
        proceedWithUserData(result.userData);
    };

    const handleClear = () => {
        setFieldValues({});
        setError('');
        setSuccess(false);
        setShowSearch(false);
        setMultipleResults(null);
    };

    return (
        <Grid container style={{ marginTop: '16px', marginBottom: '8px' }}>
            <Grid item xs={12}>
                <div
                    onClick={() => setShowSearch(!showSearch)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '12px 16px',
                        background: '#f8f5fc',
                        borderRadius: '8px',
                        border: '1px solid #e0d4ed'
                    }}
                >
                    <Typography style={{ color: '#773f98', fontWeight: 600, fontSize: '16px' }}>
                        Renewing your membership? Prefill your details
                    </Typography>
                    <IconButton size="small" style={{ color: '#773f98' }}>
                        {showSearch ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </div>
            </Grid>

            <Collapse in={showSearch} style={{ width: '100%' }}>
                <Grid container spacing={2} style={{ padding: '16px', background: '#fff', borderRadius: '0 0 8px 8px', border: '1px solid #e0d4ed', borderTop: 'none' }}>
                    <Grid item xs={12}>
                            <Typography variant="caption" style={{ color: '#888' }}>
                                Enter your details to find your existing membership
                            </Typography>
                        </Grid>

                    {searchFields.map(field => (
                        <Grid item xs={12} sm={4} key={field.key}>
                            <TextField
                                fullWidth
                                label={field.label}
                                placeholder={field.placeholder || ''}
                                value={fieldValues[field.key] || ''}
                                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                                inputProps={field.maxLength ? { maxLength: field.maxLength } : {}}
                                helperText={field.infoText || ''}
                                size="small"
                                variant="outlined"
                            />
                        </Grid>
                    ))}

                    <Grid item xs={12}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSearch}
                                disabled={loading}
                                style={{ backgroundColor: '#773f98', color: '#fff', textTransform: 'none' }}
                            >
                                {loading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
                            </Button>
                            {success && (
                                <Button
                                    size="small"
                                    onClick={handleClear}
                                    style={{ color: '#773f98', textTransform: 'none', textDecoration: 'underline' }}
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                    </Grid>

                    {error && (
                        <Grid item xs={12}>
                            <Typography style={{ color: '#f44336', fontSize: '14px' }}>{error}</Typography>
                        </Grid>
                    )}

                    {success && (
                        <Grid item xs={12}>
                            <Typography style={{ color: '#4caf50', fontSize: '14px' }}>
                                {onPrefill ? 'Details filled successfully!' : 'Details prefilled successfully! Redirecting to checkout...'}
                            </Typography>
                        </Grid>
                    )}

                    {multipleResults && multipleResults.length > 0 && (
                        <Grid item xs={12}>
                            <Typography style={{ color: '#773f98', fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                                Multiple records found. Please select one to continue:
                            </Typography>
                            <div style={{ border: '1px solid #e0d4ed', borderRadius: '4px', overflow: 'hidden' }}>
                                {multipleResults.map((result, index) => (
                                    <div
                                        key={index}
                                        onClick={() => handleSelectResult(result)}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '10px 16px',
                                            borderBottom: index < multipleResults.length - 1 ? '1px solid #e0d4ed' : 'none',
                                            cursor: 'pointer',
                                            transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8f5fc'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <Typography style={{ fontWeight: 600, color: '#0a0e51', fontSize: '14px' }}>
                                            {result.matchedPackage ? result.matchedPackage.title : (result.mapped.prefillData.name || 'Record ' + (index + 1))}
                                        </Typography>
                                        <Typography style={{ color: '#666', fontSize: '13px' }}>
                                            {result.mapped.prefillData.name} • {result.mapped.prefillData.email}
                                        </Typography>
                                    </div>
                                ))}
                            </div>
                        </Grid>
                    )}
                </Grid>
            </Collapse>
        </Grid>
    );
}

export default RenewalSearchComponent;
